import { useContext } from 'react';
import { ClipLoader } from 'react-spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { SearchContext } from '@/core/search/contexts/search-context';
import '@/core/search/components/search-input.scss';

export default function Search({
  fullWidth,
  height,
  autoFocus,
  onFocus,
  onClose,
}: {
  fullWidth?: boolean;
  /** Explicit height in px (e.g. to match sibling icon buttons) instead of the default 100%-of-parent sizing. */
  height?: number;
  autoFocus?: boolean;
  /** Called when the input gains focus — lets a caller that renders this input at a smaller resting width (e.g. the mobile topbar) grow it in response, instead of needing a separate trigger button. */
  onFocus?: () => void;
  /** Called when the input is abandoned empty (blurred with no text) or explicitly cleared — lets a caller that collapsed a bigger control down to just this input (e.g. the mobile topbar) expand it back. */
  onClose?: () => void;
}) {
  const { search, setSearch, searchResults, setHideSearchResults, searchRef, loading } =
    useContext(SearchContext)!;

  return (
    <div
      className={`position-relative${fullWidth ? '' : ' me-3'}`}
      ref={searchRef}
      onClick={() => search !== '' && setHideSearchResults(false)}
    >
      <input
        className={`search-input border border-tertiary-500 rounded px-5 focus-ring shadow-none${height === undefined ? ' h-100' : ''}`}
        style={{ width: fullWidth ? '100%' : 480, height }}
        autoFocus={autoFocus}
        placeholder="Search"
        value={search}
        onChange={event => setSearch(event.target.value)}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Enter') searchResults();
        }}
        onFocus={onFocus}
        onBlur={() => {
          if (search === '') onClose?.();
        }}
      />
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        className="position-absolute start-0 top-50 translate-middle-y ms-3 text-tertiary-500"
      />
      {loading ? (
        <span className="position-absolute end-0 top-50 translate-middle-y me-3">
          <ClipLoader size={12} color={'var(--bs-tertiary-500)'} />
        </span>
      ) : (
        // Mobile has no obvious "click outside" area to close the results
        // dropdown with (unlike desktop) — a clear button is the fast way
        // to close it there, since results hide as soon as search is empty.
        fullWidth &&
        search !== '' && (
          <button
            type="button"
            aria-label="Clear search"
            className="btn btn-link p-0 position-absolute end-0 top-50 translate-middle-y me-3 text-tertiary-500"
            onClick={() => {
              setSearch('');
              setHideSearchResults(true);
              onClose?.();
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )
      )}
    </div>
  );
}
